import { useEffect, useRef, useState } from 'react';

interface Track {
  id: number;
  title: string;
  type: string;
  year: string;
  url?: string;
}

const upcomingTracks: Track[] = [
  { id: 1, title: 'GREEN EGGS & HAM', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/' },
  { id: 2, title: 'IMPENETRABLE', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/' },
  { id: 3, title: 'PORK STEW', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/' },
  { id: 4, title: 'SYMPATHY FOR THEIR DEVILS', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/' },
  { id: 5, title: 'POPULAR SHEEP', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/' },
  { id: 6, title: 'HELIOKINESIS', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/' },
  { id: 7, title: 'QUIET FEED THEORY', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/' },
  { id: 8, title: 'DIGITAL SLOP', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/' },
  { id: 9, title: 'RELEASE (EXORCISM OF theBigBudWolf PT2)', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/' },
];

const pastTracks: Track[] = [
  { id: 1, title: 'Higher Form ill EP', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/album/hexachron-6-1-higher-form-ill' },
  { id: 2, title: 'The Present Tense 12" VINYL', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/album/the-present-tense-12-vinyl-limited' },
  { id: 3, title: 'ill In Tha Field', type: '', year: '', url: 'https://www.youtube.com/watch?v=LOlVTIHRAOE' },
  { id: 4, title: 'RAW EP', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/album/organic-raw-ep' },
  { id: 5, title: 'SAVE HIPHOP', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/album/save-hiphop' },
  { id: 6, title: 'BIGBUDWOLF', type: '', year: '', url: 'https://freshneverstale.bandcamp.com/track/bigbudwolf' },
  { id: 7, title: 'THE BLUEPRINT', type: '', year: '', url: 'https://www.youtube.com/watch?v=ZSxsbB-OrTs' },
  { id: 8, title: 'What\'s The Matter?', type: '', year: '', url: 'https://www.youtube.com/watch?v=c9TRrctvtXY' },
  { id: 9, title: 'Know Fear', type: '', year: '', url: 'https://www.youtube.com/watch?v=qpjLFGScMD8' },
  { id: 10, title: 'Higher Court', type: '', year: '', url: 'https://www.youtube.com/watch?v=ICknWWkoNh8' },
  { id: 11, title: 'The Real Real', type: '', year: '', url: 'https://www.youtube.com/watch?v=HtQmrnq8SVQ' },
  { id: 12, title: 'Anunnaki', type: '', year: '', url: 'https://www.youtube.com/watch?v=PDMT28wjdws' },
];

function HexagonCard({ track }: { track: Track }) {
  const hexClip = 'polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)';

  const isYouTube = track.url?.includes('youtube.com');
  const isBandcamp = track.url?.includes('bandcamp.com');

  const content = (
    <div className="flex flex-col items-center gap-4 group cursor-pointer">
      <div className="relative w-48 h-52 md:w-56 md:h-60 transition-transform duration-500 group-hover:scale-105">
        <div className="absolute inset-0" style={{ clipPath: hexClip }}>
          <div className="w-full h-full bg-white/80" />
        </div>
        <div className="absolute inset-[2px]" style={{ clipPath: hexClip }}>
          <div className="w-full h-full bg-gradient-to-b from-neutral-700 via-neutral-800 to-black flex flex-col items-center justify-center px-4">
            <div className="w-8 h-8 flex items-center justify-center mb-3 opacity-60">
              <i className={`${isYouTube ? 'ri-youtube-line' : isBandcamp ? 'ri-disc-line' : 'ri-music-2-line'} text-white text-2xl`} />
            </div>
            <span className="text-white text-xs tracking-[0.2rem] font-light text-center leading-relaxed">
              {track.title}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  if (track.url) {
    return (
      <a href={track.url} target="_blank" rel="nofollow noopener noreferrer" aria-label={track.title}>
        {content}
      </a>
    );
  }

  return content;
}

function TrackSlider({
  tracks,
  label,
  title,
  itemsPerPage = 3,
}: {
  tracks: Track[];
  label: string;
  title: string;
  itemsPerPage?: number;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const totalPages = Math.ceil(tracks.length / itemsPerPage);

  // -------------------------------------------------------------------------
  // IntersectionObserver – added defensive checks & cleanup
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const goToPage = (page: number) => {
    if (isSliding || page === currentPage) return;
    setIsSliding(true);
    setCurrentPage(page);
    // Reset sliding flag after the transition duration
    setTimeout(() => setIsSliding(false), 500);
  };

  const goNext = () => {
    if (currentPage < totalPages - 1) goToPage(currentPage + 1);
  };

  const goPrev = () => {
    if (currentPage > 0) goToPage(currentPage - 1);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      diff > 0 ? goNext() : goPrev();
    }
  };

  const currentTracks = tracks.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  );

  return (
    <div ref={sectionRef}>
      {/* Header */}
      <div className="mb-16">
        <div
          className={`flex items-center gap-3 mb-4 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
        >
          <div className="w-1 h-1 bg-white rounded-full" />
          <span className="text-xs tracking-[0.2rem] text-white/50 uppercase">
            {label}
          </span>
        </div>
        <h2
          className={`text-5xl font-extralight text-white transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
          style={{ fontWeight: 200 }}
        >
          {title}
        </h2>
      </div>

      {/* Slider */}
      <div
        className={`transition-all duration-700 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="relative">
          <button
            onClick={goPrev}
            disabled={currentPage === 0}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-8 z-10 w-10 h-10 flex items-center justify-center border border-white/20 rounded-full transition-all duration-300 ${
              currentPage === 0
                ? 'opacity-20 cursor-not-allowed'
                : 'hover:border-white/60 hover:bg-white/5 cursor-pointer'
            }`}
            aria-label="Previous page"
          >
            <i className="ri-arrow-left-s-line text-white text-lg" />
          </button>

          <button
            onClick={goNext}
            disabled={currentPage === totalPages - 1}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-8 z-10 w-10 h-10 flex items-center justify-center border border-white/20 rounded-full transition-all duration-300 ${
              currentPage === totalPages - 1
                ? 'opacity-20 cursor-not-allowed'
                : 'hover:border-white/60 hover:bg-white/5 cursor-pointer'
            }`}
            aria-label="Next page"
          >
            <i className="ri-arrow-right-s-line text-white text-lg" />
          </button>

          <div
            className="overflow-hidden px-8 md:px-16"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex justify-center items-center gap-8 md:gap-16 py-8 transition-all duration-500 ease-in-out"
              style={{
                opacity: isSliding ? 0 : 1,
                transform: isSliding ? 'scale(0.95)' : 'scale(1)',
              }}
            >
              {currentTracks.map((track) => (
                <HexagonCard key={track.id} track={track} />
              ))}
            </div>
          </div>
        </div>

        {/* Pagination dots */}
        <div className="flex justify-center items-center gap-3 mt-10">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              onClick={() => goToPage(i)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                i === currentPage
                  ? 'w-8 h-2 bg-white/80'
                  : 'w-2 h-2 bg-white/25 hover:bg-white/40'
              }`}
              aria-label={`Go to page ${i + 1}`}
            />
          ))}
        </div>

        {/* Page counter */}
        <div className="text-center mt-4">
          <span className="text-xs tracking-[0.15rem] text-white/30 font-mono">
            {String(currentPage + 1).padStart(2, '0')} /{' '}
            {String(totalPages).padStart(2, '0')}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Music() {
  return (
    <section id="music" className="relative min-h-screen py-20 px-6 md:px-20">
      <div className="max-w-7xl mx-auto">
        <TrackSlider tracks={upcomingTracks} label="Music" title="UPCOMING RELEASES" itemsPerPage={3} />
        <div className="mt-32">
          <TrackSlider tracks={pastTracks} label="Archive" title="PAST RELEASES" itemsPerPage={4} />
        </div>
      </div>
    </section>
  );
}
